import { useState } from 'react';
import { useToast } from '../hooks/useToast';
import Button, { IconButton } from '@ui/components/atoms/Button';
import SplitButton from '@ui/components/molecules/SplitButton';
import Dropdown from '@ui/components/molecules/Dropdown';
import MultiSelect from '@ui/components/molecules/MultiSelect';
import ComboBox from '@ui/components/molecules/ComboBox';
import DatePicker from '@ui/components/molecules/DatePicker';
import DateRangePicker from '@ui/components/molecules/DateRangePicker';
import { Checkbox, RadioGroup, Toggle } from '@ui/components/atoms/Controls';
import { Card } from '@ui/components/molecules/Card';
import Modal from '@ui/components/organisms/Modal';
import Drawer from '@ui/components/organisms/Drawer';
import Toast from '@ui/components/molecules/Toast';
import Alert from '@ui/components/molecules/Alert';
import PhotoUpload from '../components/PhotoUpload';
import { useObjectUrls } from '../components/photoUploadUtils';
import {
  Sun,
  Moon,
  Plus,
  Filter,
  CheckCircle,
  Sliders,
  Bell,
  Trash,
} from 'lucide-react';

export default function DesignSystemShowcase() {
  const [darkMode, setDarkMode] = useState(false);
  const [orderDrawerOpen, setOrderDrawerOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const { toasts, pushToast, dismissToast } = useToast();

  // Form & Control States
  const [singleDropdownVal, setSingleDropdownVal] = useState('bank_transfer');
  const [multiSelectVal, setMultiSelectVal] = useState(['Coffee', 'Bakery']);
  const [comboBoxVal, setComboBoxVal] = useState('Espresso Single Shot');
  const [singleDate, setSingleDate] = useState('2026-08-15');
  const [dateRange, setDateRange] = useState({ startDate: '2026-08-01', endDate: '2026-08-15' });
  const [checkboxVal, setCheckboxVal] = useState(true);
  const [radioVal, setRadioVal] = useState('dine_in');
  const [toggleVal, setToggleVal] = useState(true);

  const [uploadFiles, setUploadFiles] = useState([]);
  const uploadPreviews = useObjectUrls(uploadFiles);

  const removeUploadFile = (index) => {
    setUploadFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleTheme = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    if (newMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const addToast = (type = 'success', title = 'Action Completed', message = 'Component state updated successfully.') => {
    pushToast(title, message, type);
  };

  return (
    <div className="showcase-container">
      {/* Top Header */}
      <div className="showcase-header">
        <div>
          <div className="showcase-header__eyebrow">
            SEROJA UI SUITE • SCSS CLASSES & PORTAL POPOVERS
          </div>
          <h1 className="showcase-header__title">Complete Custom Component Library</h1>
          <p className="showcase-header__subtitle">
            Clean SCSS classes for all components without inline styles. React Portals prevent popup clipping.
          </p>
        </div>
        <div className="showcase-header__actions">
          <Button variant="primary" icon={darkMode ? Sun : Moon} onClick={toggleTheme}>
            {darkMode ? 'Light Mode' : 'Dark Mode'}
          </Button>
        </div>
      </div>

      <Alert type="info" title="Zero Inline Styling & Synced Design System Enabled" dismissible>
        All components now use dedicated SCSS stylesheets with zero inline styles. Popups render via <strong>React Portals</strong>.
      </Alert>

      {/* 1. Synced Buttons, Icon Buttons & Split Buttons */}
      <Card title="1. Synced Buttons, Icon Buttons & Split Buttons (8px Radius)">
        <div className="showcase-button-row">
          <Button variant="primary" icon={Plus}>Create Order</Button>
          <Button variant="secondary" icon={Filter}>Filter</Button>
          <Button variant="ghost">Ghost Action</Button>
          <Button variant="danger" icon={Trash}>Delete Item</Button>

          <IconButton icon={Sliders} variant="secondary" />
          <IconButton icon={Bell} variant="primary" />

          <SplitButton
            label="Save Transaction"
            onMainClick={() => addToast('success', 'Saved', 'Transaction saved as draft.')}
            options={[
              { label: 'Save & Print Invoice' },
              { label: 'Save & Send WhatsApp' },
              { label: 'Hold Bill (F1)' },
            ]}
            onOptionSelect={(opt) => addToast('info', 'Option Selected', opt.label)}
          />
        </div>
      </Card>

      {/* 2. Custom Portal Dropdowns, Multi-Select & Combo Box */}
      <Card title="2. Custom Dropdown, Multi-Select & Combo Box (Portal Popups)">
        <div className="showcase-grid-controls">
          <Dropdown
            label="Payment Method (Custom Dropdown)"
            value={singleDropdownVal}
            onChange={setSingleDropdownVal}
            options={[
              { label: 'Bank Transfer (BCA/Mandiri)', value: 'bank_transfer' },
              { label: 'QRIS Instant e-Wallet', value: 'qris' },
              { label: 'Cash Payment', value: 'cash' },
              { label: 'Credit Card / Debit', value: 'card' },
            ]}
          />
          <MultiSelect
            label="Categories (Multi Select Tags)"
            value={multiSelectVal}
            onChange={setMultiSelectVal}
            options={['Coffee', 'Bakery', 'Breakfast', 'Merchandise', 'Beverages']}
          />
          <ComboBox
            label="Product SKU (Search AutoComplete)"
            value={comboBoxVal}
            onChange={setComboBoxVal}
            options={[
              'Espresso Single Shot',
              'Arabica House Blend 250g',
              'Iced Spanish Latte',
              'Croissant Butter Flaky',
              'Matcha Latte Ceremonial',
            ]}
          />
        </div>
      </Card>

      {/* 3. Custom DatePicker & DateRangePicker */}
      <Card title="3. Custom Calendar Date & Date Range Pickers (No Browser Default)">
        <div className="showcase-grid-controls">
          <DatePicker
            label="Order Date (Custom Calendar)"
            value={singleDate}
            onChange={setSingleDate}
          />
          <DateRangePicker
            label="Report Period (Date Range Selector)"
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={setDateRange}
          />
        </div>
      </Card>

      {/* 4. Checkbox, Radio Group & Toggle Switch */}
      <Card title="4. Radio Buttons, Checkboxes & Toggle Switches">
        <div className="showcase-controls-column">
          <div>
            <span className="showcase-controls-column__label">Order Type (Radio Group)</span>
            <RadioGroup
              name="order_type"
              value={radioVal}
              onChange={setRadioVal}
              options={[
                { label: 'Dine-In Table', value: 'dine_in' },
                { label: 'Take Away / Express', value: 'take_away' },
                { label: 'Delivery Courier', value: 'delivery' },
              ]}
            />
          </div>
          <div className="showcase-controls-column__row">
            <Checkbox
              label="Automatically print customer receipt after payment"
              checked={checkboxVal}
              onChange={(e) => setCheckboxVal(e.target.checked)}
            />
            <Toggle
              label="Enable Real-Time Stock Warning Alerts"
              checked={toggleVal}
              onChange={(e) => setToggleVal(e.target.checked)}
            />
          </div>
        </div>
      </Card>

      {/* 5. Modals & Overlays Trigger */}
      <Card title="5. Modal & Drawer Overlay Controls (Portal Popups)">
        <div className="showcase-button-row">
          <Button variant="primary" onClick={() => setPaymentModalOpen(true)}>Open Payment Modal (Portal)</Button>
          <Button variant="secondary" onClick={() => setOrderDrawerOpen(true)}>Open Order Timeline Drawer (#AT456BB)</Button>
          <Button variant="ghost" onClick={() => addToast('success', 'Toast Notification', 'Custom toast popup triggered.')}>
            Trigger Toast Notification
          </Button>
        </div>
      </Card>

      {/* 6. Photo Upload (react-dropzone) */}
      <Card title="6. Photo Upload (react-dropzone — Drag, Click & Ctrl+V Paste)">
        <div className="showcase-photo-upload">
          <PhotoUpload
            files={uploadFiles}
            previews={uploadPreviews}
            onChange={setUploadFiles}
            onRemove={removeUploadFile}
            label="Foto Produk"
            helperText="JPG, PNG, WEBP, MP4 atau PDF — paste dari clipboard dengan Ctrl+V. Hanya 2 file gambar/video terakhir yang menampilkan preview."
          />
        </div>
      </Card>

      {/* Payment Modal */}
      <Modal
        isOpen={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        title="POS Payment Settlement"
        footer={
          <>
            <Button variant="ghost" onClick={() => setPaymentModalOpen(false)}>Cancel</Button>
            <Button variant="primary" icon={CheckCircle} onClick={() => { setPaymentModalOpen(false); addToast('success', 'Settlement Complete', 'Order payment settled via QRIS.'); }}>
              Confirm Settlement
            </Button>
          </>
        }
      >
        <p className="modal-text-desc">
          Select settlement method for Order <strong>#TRX-9821</strong> (Total: $120.99):
        </p>
        <Dropdown
          label="Settlement Method"
          value={singleDropdownVal}
          onChange={setSingleDropdownVal}
          options={['Bank Transfer', 'QRIS Instant', 'Cash', 'Credit Card']}
        />
      </Modal>

      {/* Order Timeline Drawer */}
      <Drawer
        isOpen={orderDrawerOpen}
        onClose={() => setOrderDrawerOpen(false)}
        title="#AT456BB"
        footer={
          <Button variant="primary" fullWidth onClick={() => setOrderDrawerOpen(false)}>Close Drawer</Button>
        }
      >
        <div className="timeline">
          <div className="timeline__item">
            <div className="timeline__item-dot timeline__item-dot--active" />
            <div className="timeline__item-title">Order Processed</div>
            <div className="timeline__item-desc">Products are packed and ready for dispatch.</div>
          </div>
          <div className="timeline__item">
            <div className="timeline__item-dot timeline__item-dot--completed" />
            <div className="timeline__item-title">Payment Verified</div>
            <div className="timeline__item-desc">Payment confirmed via Bank Transfer.</div>
          </div>
          <div className="timeline__item">
            <div className="timeline__item-dot timeline__item-dot--completed" />
            <div className="timeline__item-title">Order Placed</div>
            <div className="timeline__item-desc">Order created by Cameron Williamson.</div>
          </div>
        </div>
      </Drawer>

      <Toast toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}
